import { action, computed, IReactionDisposer, observable } from 'mobx';
import { WithoutPromise } from 'mobx-task';
import { isNil } from 'ramda';
import { onSuccessReaction, ResultAwareTask } from '~common/helper/mobx-tasks';
import { BaseStore } from '~web/store/base/base-store';

/**
 * A store wrapper for a `mobx-task` with some utility functions
 */
export class TaskStore<R> extends BaseStore {
  @observable
  task: ResultAwareTask<R>;
  @observable
  result: R;
  protected firstArg?: any;
  protected secondArg?: any;
  protected thirdArg?: any;
  protected successReaction: IReactionDisposer;

  /**
   * The task can be set in the constructor
   * @param task
   * @param firstArg
   * @param secondArg
   * @param thirdArg
   */
  constructor(task?: ResultAwareTask<R>, firstArg?: any, secondArg?: any, thirdArg?: any) {
    super();
    this.setTask(task, firstArg, secondArg, thirdArg);
  }

  @computed
  get error(): Error | undefined {
    return this.task ? this.task.error : undefined;
  }

  @computed
  get pending(): boolean | undefined {
    return this.task ? this.task.pending : undefined;
  }

  @computed
  get rejected(): boolean | undefined {
    return this.task ? this.task.rejected : undefined;
  }

  @computed
  get resolved(): boolean | undefined {
    return this.task ? this.task.resolved : undefined;
  }

  setTask(task: ResultAwareTask<R>, firstArg?: any, secondArg?: any, thirdArg?: any): void {
    // reset the old task, if any
    this.reset();
    this.disposeSuccessReaction();
    if (task) {
      this.task = task;
      this.firstArg = firstArg;
      this.secondArg = secondArg;
      this.thirdArg = thirdArg;
      this.setSuccessReaction();
    }
  }

  @action
  setResult(result: WithoutPromise<R>): void {
    this.result = result as R;
    this.task.setState({ state: 'resolved', result, error: undefined });
  }

  @action
  execute(firstArg?: any, secondArg?: any, thirdArg?: any): void {
    if (!this.task.pending) {
      this.task(firstArg || this.firstArg, secondArg || this.secondArg, thirdArg || this.thirdArg);
    }
  }

  reset(): void {
    if (this.task) {
      this.task.reset();
    }
    this.result = undefined;
  }

  clear(): void {
    this.reset();
  }

  protected disposeSuccessReaction(): void {
    if (!isNil(this.successReaction)) {
      this.successReaction();
      this.successReaction = null;
    }
  }

  protected setSuccessReaction(): void {
    this.successReaction = onSuccessReaction(this.task, (result: R) => {
      this.result = result;
    });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/web/src/js/store/base/task-store.ts